package com.swetdot.swetgram.data.api

import com.swetdot.swetgram.protocol.ClientHeaders
import com.swetdot.swetgram.protocol.ProtocolJson
import io.ktor.client.HttpClient
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.defaultRequest
import io.ktor.client.plugins.websocket.WebSockets
import io.ktor.client.request.header
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.serialization.kotlinx.json.json

/**
 * Identifies this build to the server. The contract marks all three headers `required: true`
 * on every operation, and `client_upgrade_required` (426) is keyed off them -- so they are not
 * telemetry, they are how the server decides whether to serve us at all.
 */
data class ClientInfo(
    /** Native contract values are `ios` / `android`; the server also supports `web` sessions. */
    val platform: String,
    val version: String,
    val build: String,
)

object ApiConfig {
    const val BASE_URL = "https://swetgram.com/api/v1"

    /**
     * Centrifugo, behind the same Caddy that terminates TLS for the API. It is the only path of
     * Centrifugo's that is published; its HTTP API stays on the server's loopback.
     */
    const val REALTIME_URL = "wss://swetgram.com/connection/websocket"

    /** DECISIONS §5: "Standard request and message-send timeout is 15 seconds." */
    const val TIMEOUT_MS = 15_000L
}

/**
 * Builds the shared HttpClient.
 *
 * [tokenProvider] is read per request rather than captured, so a token refreshed mid-session
 * is picked up without rebuilding the client.
 */
fun createHttpClient(
    engineClient: HttpClient,
    clientInfo: ClientInfo,
    baseUrl: String = ApiConfig.BASE_URL,
    tokenProvider: suspend () -> String?,
): HttpClient = engineClient.config {
    expectSuccess = false

    install(ContentNegotiation) {
        // The SAME Json the contract module defines. A locally-configured one would drift from
        // the server's and break on the first field added in a minor version.
        json(ProtocolJson)
    }

    install(HttpTimeout) {
        requestTimeoutMillis = ApiConfig.TIMEOUT_MS
        connectTimeoutMillis = ApiConfig.TIMEOUT_MS
        socketTimeoutMillis = ApiConfig.TIMEOUT_MS
    }

    defaultRequest {
        url(baseUrl.trimEnd('/') + "/")
        contentType(ContentType.Application.Json)
        header(ClientHeaders.PLATFORM, clientInfo.platform)
        header(ClientHeaders.VERSION, clientInfo.version)
        header(ClientHeaders.BUILD, clientInfo.build)
    }
}

/**
 * A SECOND client, for the realtime socket only.
 *
 * It cannot be the one above. That one sets `socketTimeoutMillis = 15s`, which applies to a
 * WebSocket too -- and Centrifugo pings roughly every 25 seconds, so the connection would be
 * torn down between pings, forever, while every REST call kept working. Its `defaultRequest`
 * would also rewrite the socket URL to the `/api/v1` base.
 *
 * No timeouts here at all: a messenger socket sitting idle for minutes is the normal case.
 */
fun createRealtimeHttpClient(engineClient: HttpClient): HttpClient = engineClient.config {
    install(WebSockets)
}

/**
 * A THIRD client, for fetching images the CDN serves.
 *
 * Same reasoning as [UploadRepository][com.swetdot.swetgram.data.media.UploadRepository], in the
 * opposite direction. Avatars and message photos live on `media.swetgram.com`, a different origin
 * from the API, and [createHttpClient] would attach `Authorization: Bearer <access token>` plus the
 * three `X-Client-*` headers to every one of those GETs -- handing a live session token to an
 * origin that has no business holding one, on requests that are made constantly and unattended.
 * Its `defaultRequest` would also rewrite each absolute media URL against the `/api/v1` base, so
 * nothing would load at all.
 *
 * `expectSuccess = false` because Coil's network fetcher reads the status code itself and turns a
 * 404 into a rendered error state; a thrown `ClientRequestException` would reach it as a generic
 * failure instead.
 *
 * No `HttpTimeout`: a photo on a slow connection is exactly the case the 15-second socket timeout
 * of [ApiConfig.TIMEOUT_MS] would kill, and Coil already cancels a request when the composable
 * leaves the screen.
 */
fun createMediaHttpClient(engineClient: HttpClient): HttpClient = engineClient.config {
    expectSuccess = false
}
